import React from 'react';
import ValueCard from '../../data-widgets/ValueCard';
import { useTranslation } from 'react-i18next';
import Skeleton from '@mui/material/Skeleton';
import useDistanceUnitValue from '@common/hooks/useDistanceUnitValue';
import { DistanceValueUnit } from '@common/enums/configuration/units';
import Stack from '@mui/material/Stack';
import { Theme, useMediaQuery } from '@mui/material';

const RobotCards: React.FC<{
  filterApplied?: boolean;
  robotCount: number;
  cleanedArea: number;
  operatingTime: number;
  locationCount: number;
  loading?: boolean;
  fixGrid?: boolean;
}> = ({
  fixGrid,
  filterApplied,
  robotCount,
  cleanedArea,
  operatingTime,
  locationCount,
  loading,
}) => {
  const { t } = useTranslation();
  const matchesMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const { converted: totalCleanedArea, label: totalCleanedAreaLabel } = useDistanceUnitValue(
    cleanedArea ?? 0,
    DistanceValueUnit.METERS_SQUARED,
  );

  if (loading) {
    return (
      <Stack
        direction={matchesMdUp || fixGrid ? 'row' : 'column'}
        sx={{ height: { xs: '800px', sm: '400px' } }}
        spacing={2}
        width="100%"
      >
        <Stack spacing={2} width="100%" flex={1}>
          <Skeleton sx={{ width: '100%', flex: 1 }} variant="rounded" />
          <Skeleton sx={{ width: '100%', flex: 1 }} variant="rounded" />
        </Stack>
        <Stack spacing={2} width="100%" flex={1}>
          <Skeleton sx={{ width: '100%', flex: 1 }} variant="rounded" />
          <Skeleton sx={{ width: '100%', flex: 1 }} variant="rounded" />
        </Stack>
      </Stack>
    );
  }

  const totalOperatingTime =
    (operatingTime ?? 0) / 3600 >= 1 ? (operatingTime ?? 0) / 3600 : (operatingTime ?? 0) / 60;
  const unitLabel = (operatingTime ?? 0) / 3600 >= 1 ? t('label-hours-long') : t('label-minutes');

  return (
    <React.Fragment>
      <Stack direction={matchesMdUp || fixGrid ? 'row' : 'column'} spacing={2} width="100%">
        <Stack spacing={2} width="100%">
          <ValueCard
            valueTestId="robot-count-value"
            value={robotCount}
            subtitle={filterApplied ? t('label-filter-applied') : t('label-all-time')}
            label={t('label-robot-count')}
            unit={t('table-header-robot-count')}
          />
          <ValueCard
            label={t('label-total-deployment-locations')}
            value={locationCount}
            unit={t('title-locations')}
            subtitle={filterApplied ? t('label-filter-applied') : t('label-all-time')}
          />
        </Stack>
        <Stack spacing={2} width="100%">
          <ValueCard
            label={t('label-total-operating-time')}
            value={Math.round(totalOperatingTime)}
            unit={unitLabel}
            subtitle={filterApplied ? t('label-filter-applied') : t('label-all-time')}
          />
          <ValueCard
            label={t('label-total-cleaned-area')}
            subtitle={filterApplied ? t('label-filter-applied') : t('label-all-time')}
            value={Math.round(totalCleanedArea)}
            unit={totalCleanedAreaLabel}
          />
        </Stack>
      </Stack>
    </React.Fragment>
  );
};

export default RobotCards;
